import * as THREE from "../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";

import {T, t} from "./i18n/index.js";
// Potrees Clip-objekter (Clip Volume/Clip Box = scene.volumes med clip, Clip Polygon = scene.polygonClipVolumes) som
// objekter i Registreringer/Målinger-træet.
//
// Clip-objektet forbliver et ægte Potree-objekt med Potrees egen klippe-/fremhævningslogik og selection - denne bro
// giver det blot en række i træet (som den native 2D-profil, se nativeProfile.js), så det kan vælges, skjules, genåbnes
// og slettes gennem den normale objekt-livscyklus:
//
//   træets øje      ->  clip.visible
//   træets slet     ->  scene.removeVolume / removePolygonClipVolume
//   clip fjernet    ->  featuren fjernes (fx "fjern alle clips")
//   valgt i træet   ->  volumen markeres i vieweren (Potrees egen selection)
//
// Selve clip-STATE (Clip-opgave/-metode) er globale viewer-indstillinger og ligger ikke på objekterne.

export const CLIP_TOOL_ID = "digitize_clip";

export const ClipTool = {
	id: CLIP_TOOL_ID,
	name: T("Clip"),
	category: "hidden",
	kind: "measurement",
	icon: "clip_volume.svg",
	resultGeometryType: "Point",
	featureType: T("Clip"),
	tooltip: T("Clip-objekt (volumen, polygon eller skærmboks)."),
	customCapture(){
		// Intet: clip-objekter oprettes af Potrees egne værktøjer (volumeTool/clippingTool/boxSelectTool).
	},
};

DigitizationTools.register(ClipTool);

// Clip-objektets omtrentlige position (til træets geometri; bruges ikke af Potrees clipping).
function centerOf(object, kind){
	if(kind === "polygon"){
		const markers = object.markers || [];
		if(markers.length > 0){
			const c = new THREE.Vector3();
			markers.forEach((m) => c.add(m.position));
			return c.multiplyScalar(1 / markers.length);
		}
		return object.camera ? object.camera.position.clone() : new THREE.Vector3();
	}
	return object.position ? object.position.clone() : new THREE.Vector3();
}

// Tegner intet selv (clip-objektet har sin egen visning) - bærer kun træ-rækkens tilstand.
class NativeClipVisual extends FeatureVisual {
	constructor(viewer, object, kind){
		super("Point");
		this.editable = false;
		this._viewer = viewer;
		this._object = object;
		this._kind = kind;
		this.userData.nativeClip = {object, kind};
	}

	rawCoordinates(){
		const c = centerOf(this._object, this._kind);
		return [[c.x, c.y, c.z]];
	}

	toCoordinates(){
		return this.rawCoordinates()[0];
	}

	setSelected(value){
		super.setSelected(value);
		const input = this._viewer.inputHandler;
		if(!input || this._kind !== "volume") return;
		const selected = input.selection.indexOf(this._object) !== -1;
		if(value){
			if(!selected){
				input.deselectAll();
				input.toggleSelection(this._object);
			}
		}else if(selected){
			input.deselectAll();
		}
	}
}

// Spejler et clip-objekt som en feature. Idempotent pr. objekt. kind: "volume" | "polygon". Returnerer feature-id'et.
export function registerNativeClip(dm, object, kind, labelOverride = null){
	if(!object || (kind !== "volume" && kind !== "polygon")) return null;
	// interne clip-volumener (fx graverapportens markering af udgravning/påfyldning) er ikke Clip-værktøjets og får ingen række
	if(object.pvInternal) return null;
	// Snitplaner spejles af nativeSnit.js (ét objekt, én række) - ikke også som "Clip Volume N"
	if(object.pvSnit) return null;

	for(const [featureId, existing] of dm.visuals){
		if(existing.userData.nativeClip && existing.userData.nativeClip.object === object) return featureId;
	}

	const scene = dm.viewer.scene;
	const visual = new NativeClipVisual(dm.viewer, object, kind);
	dm.scene.add(visual);

	const labelOf = (obj, k) => k === "polygon" ? t("Clip Polygon") : (obj.name === "Clip Box" ? t("Clip Box") : t("Clip Volume"));
	const label = labelOverride || labelOf(object, kind);
	const number = [...dm.visuals.values()].filter((v) => {
		const nc = v.userData.nativeClip;
		return !!nc && (nc.label || labelOf(nc.object, nc.kind)) === label;
	}).length + 1;

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: ClipTool.category,
		tool: CLIP_TOOL_ID,
		featureType: ClipTool.featureType,
		name: `${label} ${number}`,
		attributes: {clipUuid: object.uuid, clipType: kind},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	visual.userData.nativeClip.label = label;

	// Træets øje styrer clip-objektets egen synlighed.
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => object.visible !== false,
		set: (value) => { object.visible = !!value; },
	});

	dm._wireFeatureLifecycle(feature, visual);
	// et clip-center er ikke et snap-mål
	dm.removeFeatureSnapTargets(feature.id);

	const removedEvent = kind === "polygon" ? "polygon_clip_volume_removed" : "volume_removed";
	let released = false;
	const release = () => {
		if(released) return false;
		released = true;
		scene.removeEventListener(removedEvent, onClipRemoved);
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		return true;
	};
	const onClipRemoved = (e) => {
		if(e.volume !== object) return;
		if(!release()) return;
		if(dm.getVisual(feature.id)) dm.removeFeature(feature.id);
	};
	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		if(!release()) return;
		const input = dm.viewer.inputHandler;
		if(input && input.selection.indexOf(object) !== -1) input.deselectAll();
		if(kind === "polygon"){
			if(scene.polygonClipVolumes.indexOf(object) !== -1) scene.removePolygonClipVolume(object);
		}else if(scene.volumes.indexOf(object) !== -1){
			scene.removeVolume(object);
		}
	};
	scene.addEventListener(removedEvent, onClipRemoved);
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);

	dm.dispatchEvent({type: "feature_created", feature, visual});

	return feature.id;
}

// {object, kind} bag en feature (null for alle andre registreringer).
export function getNativeClip(dm, featureId){
	const visual = dm.getVisual(featureId);
	return visual && visual.userData.nativeClip ? visual.userData.nativeClip : null;
}
